import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuthModal } from '../../../contexts/AuthModalContext';

const tickerItems = [
  'Behavioral · Google L4 style',
  'DSA · Senior SWE · Python',
  'System design · Payments platform',
  'HR screen · Fintech startup',
  'PM · Metrics & tradeoffs',
];

const boilerplates = {
  python: `def two_sum(nums, target):\n    # Write your solution here\n    return []`,
  javascript: `function twoSum(nums, target) {\n    // Write your solution here\n    return [];\n}`,
  cpp: `#include <vector>\nusing namespace std;\n\nclass Solution {\npublic:\n    vector<int> twoSum(vector<int>& nums, int target) {\n        // Write your solution here\n        return {};\n    }\n};`,
  go: `package main\n\nfunc twoSum(nums []int, target int) []int {\n    // Write your solution here\n    return []int{}\n}`
};

const HeroSection = () => {
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();
  const containerRef = useRef(null);
  const hasTriggeredRef = useRef(false);

  // Auto-typing and simulation states
  const [selectedLanguage, setSelectedLanguage] = useState('python');
  const [code, setCode] = useState('def two_sum(nums, target):\n    ');
  const [isAutoplay, setIsAutoplay] = useState(false);
  const [autoplayStep, setAutoplayStep] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ x: 50, y: 50 });
  const [cursorVisible, setCursorVisible] = useState(false);

  // Interactive console states
  const [chatMessages, setChatMessages] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [runState, setRunState] = useState('idle');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(1122);

  // Show transcript for 2 seconds when a new chat message comes
  useEffect(() => {
    if (chatMessages.length === 0) return;
    setIsTranscriptVisible(true);
    const timer = setTimeout(() => {
      setIsTranscriptVisible(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [chatMessages]);

  // Timer ticking logic
  useEffect(() => {
    if (showReport) return;
    const interval = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 0) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showReport]);

  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Trigger autoplay once when the section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggeredRef.current) {
          hasTriggeredRef.current = true;
          startAutoplay();
        }
      },
      { threshold: 0.15 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const startAutoplay = () => {
    setIsAutoplay(true);
    setAutoplayStep(0);
    setIsSubmitted(false);
    setShowReport(false);
    setSecondsRemaining(1122);
  };

  const stopAutoplay = () => {
    setIsAutoplay(false);
    setCursorVisible(false);
  };

  // State machine for Autoplay Tour
  useEffect(() => {
    if (!isAutoplay) return;
    let timer;

    if (autoplayStep === 0) {
      setSelectedLanguage('python');
      setCode('def two_sum(nums, target):\n    ');
      setChatMessages([{
        id: 1, sender: 'Interviewer',
        text: "Welcome! Let's start with a coding problem: Two Sum. I have loaded it on your screen. Try to implement a correct solution.",
        time: '10:31 AM'
      }]);
      setTestCases([]);
      setRunState('idle');
      setCursorVisible(false);
      timer = setTimeout(() => setAutoplayStep(1), 6000);

    } else if (autoplayStep === 1) {
      const targetCode = `def two_sum(nums, target):\n    for i in range(len(nums)):\n        for j in range(i, len(nums)):\n            if nums[i] + nums[j] == target:\n                return [i, j]\n    return []`;
      let currentIdx = 26;
      setCode(targetCode.substring(0, currentIdx));
      const typingInterval = setInterval(() => {
        if (currentIdx < targetCode.length) {
          currentIdx += 2;
          if (currentIdx > targetCode.length) currentIdx = targetCode.length;
          setCode(targetCode.substring(0, currentIdx));
        } else {
          clearInterval(typingInterval);
          timer = setTimeout(() => setAutoplayStep(2), 1500);
        }
      }, 70);
      return () => { clearInterval(typingInterval); clearTimeout(timer); };

    } else if (autoplayStep === 2) {
      setCursorVisible(true);
      setCursorPosition({ x: 50, y: 50 });
      timer = setTimeout(() => {
        setCursorPosition({ x: 42, y: 84 });
        timer = setTimeout(() => setAutoplayStep(3), 2000);
      }, 200);

    } else if (autoplayStep === 3) {
      setRunState('running');
      setCursorVisible(false);
      setTestCases([
        { id: 1, name: 'Test Case 1', status: 'running' },
        { id: 2, name: 'Test Case 2', status: 'running' },
        { id: 3, name: 'Test Case 3', status: 'running' },
      ]);
      timer = setTimeout(() => setAutoplayStep(4), 3000);

    } else if (autoplayStep === 4) {
      setRunState('done');
      setTestCases([
        { id: 1, name: 'Test Case 1', status: 'passed', input: 'nums = [2,7,11,15], target = 9', expected: '[0,1]', actual: '[0,1]' },
        { id: 2, name: 'Test Case 2', status: 'passed', input: 'nums = [3,2,4], target = 6', expected: '[1,2]', actual: '[1,2]' },
        { id: 3, name: 'Test Case 3', status: 'failed', input: 'nums = [3,3], target = 6', expected: '[0,1]', actual: '[0,0]' },
      ]);
      setChatMessages(prev => [...prev, {
        id: 2, sender: 'Interviewer',
        text: "It seems your solution returned [0,0] instead of [0,1] for test case 3. You are reusing index i in your inner loop. Can you fix this?",
        time: '10:33 AM'
      }]);
      timer = setTimeout(() => setAutoplayStep(5), 8000);

    } else if (autoplayStep === 5) {
      setChatMessages(prev => [...prev, {
        id: 3, sender: 'You',
        text: "Ah! My inner loop starts at i instead of i + 1, so it reuses the same element. Let me correct the range.",
        time: '10:33 AM'
      }]);
      timer = setTimeout(() => {
        setCode(`def two_sum(nums, target):\n    for i in range(len(nums)):\n        for j in range(i + 1, len(nums)):\n            if nums[i] + nums[j] == target:\n                return [i, j]\n    return []`);
        timer = setTimeout(() => setAutoplayStep(6), 3000);
      }, 5000);

    } else if (autoplayStep === 6) {
      setCursorVisible(true);
      setCursorPosition({ x: 50, y: 50 });
      timer = setTimeout(() => {
        setCursorPosition({ x: 42, y: 84 });
        timer = setTimeout(() => setAutoplayStep(7), 2000);
      }, 200);

    } else if (autoplayStep === 7) {
      setRunState('running');
      setCursorVisible(false);
      setTestCases(Array.from({ length: 8 }, (_, i) => ({
        id: i + 1, name: `Test Case ${i + 1}`, status: 'running'
      })));
      timer = setTimeout(() => setAutoplayStep(8), 3000);

    } else if (autoplayStep === 8) {
      setRunState('done');
      setTestCases(Array.from({ length: 8 }, (_, i) => ({
        id: i + 1, name: `Test Case ${i + 1}`, status: 'passed'
      })));
      setChatMessages(prev => [...prev,
        { id: 4, sender: 'You', text: "Fixed! All 8 test cases are now passing.", time: '10:34 AM' },
        { id: 5, sender: 'Interviewer', text: "Great job resolving that index reuse bug. Click Submit to finish this round and see your performance scorecard.", time: '10:34 AM' }
      ]);
      timer = setTimeout(() => setAutoplayStep(9), 8000);

    } else if (autoplayStep === 9) {
      setCursorVisible(true);
      setCursorPosition({ x: 50, y: 50 });
      timer = setTimeout(() => {
        setCursorPosition({ x: 74, y: 84 });
        timer = setTimeout(() => setAutoplayStep(10), 2000);
      }, 200);

    } else if (autoplayStep === 10) {
      setCursorVisible(false);
      setIsSubmitted(true);
      timer = setTimeout(() => {
        setShowReport(true);
        setAutoplayStep(11);
      }, 3500);
    } else if (autoplayStep === 11) {
      timer = setTimeout(() => {
        startAutoplay();
      }, 8000);
    }

    return () => clearTimeout(timer);
  }, [autoplayStep, isAutoplay]);

  // Interactive user actions
  const handleLangChange = (lang) => {
    stopAutoplay();
    setSelectedLanguage(lang);
    setCode(boilerplates[lang] || '');
    setRunState('idle');
    setTestCases([]);
    setIsSubmitted(false);
    setShowReport(false);
  };

  const handleRunCode = () => {
    stopAutoplay();
    setRunState('running');
    setTestCases([
      { id: 1, name: 'Test Case 1', status: 'running' },
      { id: 2, name: 'Test Case 2', status: 'running' },
      { id: 3, name: 'Test Case 3', status: 'running' },
    ]);
    setTimeout(() => {
      setRunState('done');
      const correctLoop = code.includes('i + 1') || code.includes('i+1') || code.includes('j = i + 1');
      const correctHash = code.includes('seen') || code.includes('dict') || code.includes('map') || code.includes('unordered_map') || code.includes('hash');
      if (correctLoop || correctHash) {
        setTestCases([
          { id: 1, name: 'Test Case 1', status: 'passed', input: 'nums = [2,7,11,15], target = 9', expected: '[0,1]', actual: '[0,1]' },
          { id: 2, name: 'Test Case 2', status: 'passed', input: 'nums = [3,2,4], target = 6', expected: '[1,2]', actual: '[1,2]' },
          { id: 3, name: 'Test Case 3', status: 'passed', input: 'nums = [3,3], target = 6', expected: '[0,1]', actual: '[0,1]' },
        ]);
      } else {
        setTestCases([
          { id: 1, name: 'Test Case 1', status: 'passed', input: 'nums = [2,7,11,15], target = 9', expected: '[0,1]', actual: '[0,1]' },
          { id: 2, name: 'Test Case 2', status: 'passed', input: 'nums = [3,2,4], target = 6', expected: '[1,2]', actual: '[1,2]' },
          { id: 3, name: 'Test Case 3', status: 'failed', input: 'nums = [3,3], target = 6', expected: '[0,1]', actual: '[0,0]' },
        ]);
        setChatMessages(prev => [...prev, {
          id: Date.now(), sender: 'Interviewer',
          text: "Your code failed on test case 3. Try to verify your loop start range to avoid index reuse.",
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }]);
      }
    }, 1200);
  };

  const handleSubmitCode = () => {
    stopAutoplay();
    setIsSubmitted(true);
    setTimeout(() => setShowReport(true), 1500);
  };

  const handleResetPractice = () => {
    stopAutoplay();
    setIsSubmitted(false);
    setShowReport(false);
    setRunState('idle');
    setTestCases([]);
    setCode(boilerplates[selectedLanguage]);
    setSecondsRemaining(1122);
  };

  const isOptimized = code.includes('map') || code.includes('seen') || code.includes('dict') || code.includes('unordered_map') || code.includes('hash');

  const latestMessage = chatMessages[chatMessages.length - 1];
  const latestMessageText = latestMessage?.text || '';
  const latestMessageSender = latestMessage?.sender || '';

  return (
    <section className="relative isolate overflow-hidden bg-[var(--lp-bg)] pt-28 pb-16 sm:pt-32 lg:pb-8 lg:pt-36">
      <style>{`
        #hero-playground .font-semibold:not(h1) { font-weight: 600 !important; }
        #hero-playground .font-medium:not(h1) { font-weight: 500 !important; }
        #hero-playground .font-bold:not(h1) { font-weight: 700 !important; }
        #hero-playground h3 { font-weight: 600 !important; }
        .textarea-ide::selection { background-color: rgba(16, 185, 129, 0.2); }
      `}</style>
      <div className="lp-grain absolute inset-0 -z-10" />
      <div className="absolute -left-32 top-20 h-[420px] w-[420px] rounded-full bg-[var(--lp-accent)]/8 blur-[100px]" />
      <div className="absolute right-0 top-1/3 h-[300px] w-[300px] rounded-full bg-[var(--lp-accent)]/10 blur-[80px] dark:opacity-40" />

      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">

        {/* ── TOP: Centered Hero Text ── */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-6xl mx-auto"
        >
          {/* IITians badge */}
          <div className="inline-flex items-center gap-1.5 mb-6 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/30 text-xs font-medium text-emerald-700 dark:text-emerald-400 shadow-sm">
            <span>Built by IITians with</span>
            <span>❤️</span>
            <span>for all</span>
          </div>

          <h1 className="text-[2.75rem] font-semibold leading-[0.95] tracking-tight text-[var(--lp-ink)] sm:text-6xl lg:text-[4.25rem]">
            Build interview confidence
            <span className="block text-[var(--lp-accent)]">before the real round.</span>
          </h1>

          <p className="mt-2 max-w-xl mx-auto text-lg leading-relaxed text-[var(--lp-ink-soft)] lg:text-[1.2rem]">
            prepxai runs realistic voice, coding, and design rounds - then hands you a recruiter-grade scorecard before the real meeting.
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
            <Button
              size="lg"
              onClick={() => openAuthModal('register', { redirectTo: '/interview-setup' })}
              className="h-12 rounded-full bg-[var(--lp-accent)] px-8 text-sm font-semibold text-white shadow-[0_20px_50px_-12px_var(--lp-accent-glow)] hover:bg-[var(--lp-accent)]/90"
              iconName="ArrowRight"
              iconPosition="right"
            >
              Create account
            </Button>
            <button
              type="button"
              onClick={() => navigate('/interview-setup')}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[var(--lp-line)] bg-transparent px-6 text-sm font-semibold text-[var(--lp-ink)] transition-colors hover:bg-[var(--lp-surface)]"
            >
              <Icon name="Play" size={16} className="text-[var(--lp-accent)]" />
              Run a mock now
            </button>
          </div>

          <div className="mt-8 flex items-center justify-center gap-6 text-[var(--lp-ink-soft)] text-sm select-none">
            <span className="flex items-center gap-1.5">
              
             No credit card required
            </span>
            <span className="hidden sm:inline text-[var(--lp-line)]">|</span>
            <span className="hidden sm:inline">Dsa, Data science, and many more soon.</span>
          </div>
        </motion.div>

        {/* ── BOTTOM: Autoplay Coding Playground Tour ── */}
        <motion.div
          ref={containerRef}
          id="hero-playground"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 max-w-6xl mx-auto relative font-sans w-full"
        >
          <div className="w-full bg-white dark:bg-[#0c0d10] border border-[var(--lp-line)] rounded-2xl shadow-lg overflow-hidden flex flex-col justify-between min-h-[600px] relative">

            {/* Virtual Mouse Pointer */}
            {cursorVisible && (
              <div
                className="absolute pointer-events-none z-50 transition-all duration-[1800ms] ease-in-out"
                style={{ left: `${cursorPosition.x}%`, top: `${cursorPosition.y}%` }}
              >
                <svg className="w-5 h-5 drop-shadow-md text-emerald-500 fill-current" viewBox="0 0 24 24">
                  <path d="M13.682 17.514l-2.736-5.748-3.946 3.946V2.348l12.728 12.728h-6.046l2.736 5.748-2.736.69z" />
                </svg>
              </div>
            )}

            {/* Floating Transcript */}
            <AnimatePresence>
              {isTranscriptVisible && latestMessageText && !showReport && (
                <div className="absolute bottom-[92px] left-[50%] -translate-x-1/2 w-full max-w-lg px-4 z-40 pointer-events-none select-none">
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="bg-slate-900/95 dark:bg-black/95 backdrop-blur-md rounded-xl p-3 shadow-xl border border-slate-800/40 dark:border-white/10 text-center">
                      <p className="text-[13px] text-white/95 font-medium leading-relaxed font-sans">
                        <span className="text-emerald-400 font-bold mr-1.5">
                          {latestMessageSender === 'You' ? 'You' : 'Interviewer'}:
                        </span>
                        {latestMessageText}
                      </p>
                    </div>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>

            {/* Mockup Header */}
            <div className="h-12 border-b border-[var(--lp-line)] px-4 flex items-center justify-between bg-white dark:bg-[#111216] select-none">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 rounded-full bg-red-400" />
                  <span className="w-3.5 h-3.5 rounded-full bg-amber-400" />
                  <span className="w-3.5 h-3.5 rounded-full bg-emerald-400" />
                </div>
                <div className="hidden sm:flex items-center gap-2 ml-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-sm font-semibold text-[var(--lp-ink)]">AI Interview Session — Senior Software Engineer</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <button onClick={() => { if (isAutoplay) { stopAutoplay(); } else { startAutoplay(); } }}
                    className="p-1 text-slate-500 hover:text-[var(--lp-ink)] hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition"
                    title={isAutoplay ? "Pause Tour" : "Play Tour"}>
                    <Icon name={isAutoplay ? "Pause" : "Play"} size={13} />
                  </button>
                  <button onClick={startAutoplay}
                    className="p-1 text-slate-500 hover:text-[var(--lp-ink)] hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition"
                    title="Restart Tour">
                    <Icon name="RotateCcw" size={13} />
                  </button>
                </div>
                <div className="flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live
                </div>
                <div className="flex items-center text-[var(--lp-ink-soft)] font-mono text-[13px]">
                  <Icon name="Clock" size={13} className="text-emerald-500 mr-1.5" />
                  <span>{formatTime(secondsRemaining)}</span>
                </div>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {showReport ? (
                /* ── SCORECARD REPORT ── */
                <motion.div key="scorecard-report" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
                  className="flex-1 flex flex-col p-6 bg-slate-50/50 dark:bg-[#07080a]"
                >
                  <div className="flex items-center justify-between border-b border-[var(--lp-line)] pb-4">
                    <div>
                      <h3 className="text-xl font-bold text-[var(--lp-ink)] flex items-center gap-2">
                        <Icon name="FileSpreadsheet" className="text-emerald-500" size={18} /> Interview Scorecard
                      </h3>
                      <p className="text-sm text-[var(--lp-ink-soft)] mt-0.5">Round completed: Two Sum (Easy)</p>
                    </div>
                    <span className="text-xsfont-bold tracking-wider px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-600 dark:bg-emerald-950/20 dark:border-emerald-800/40 dark:text-emerald-400 rounded">
                      Validated by AI Coach
                    </span>
                  </div>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-6 mt-6 min-h-0">
                    <div className="md:col-span-5 border border-[var(--lp-line)] bg-white dark:bg-[#111216] rounded-xl p-5 flex flex-col items-center justify-center text-center shadow-sm">
                      <div className="relative flex items-center justify-center">
                        <svg className="w-36 h-36 transform -rotate-90">
                          <circle cx="72" cy="72" r="62" stroke="currentColor" strokeWidth="6" className="text-slate-100 dark:text-slate-800" fill="transparent" />
                          <circle cx="72" cy="72" r="62" stroke="currentColor" strokeWidth="6" className="text-emerald-500" fill="transparent"
                            strokeDasharray={2 * Math.PI * 62} strokeDashoffset={2 * Math.PI * 62 * (1 - (isOptimized ? 0.98 : 0.88))} />
                        </svg>
                        <div className="absolute flex flex-col items-center">
                          <span className="text-3xl font-bold text-[var(--lp-ink)]">{isOptimized ? '9/10' : '8/10'}</span>
                        </div>
                      </div>
                      <div className="w-full space-y-3.5 mt-5">
                        {[{ l: 'Correctness', v: '100 / 100', w: '100%' }, { l: 'Communication & Agility', v: '92 / 100', w: '92%' }].map(m => (
                          <div key={m.l} className="space-y-1">
                            <div className="flex items-center justify-between text-[11px] font-bold"><span className="text-[var(--lp-ink-soft)]">{m.l}</span><span className="text-emerald-500">{m.v}</span></div>
                            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: m.w }} /></div>
                          </div>
                        ))}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-[11px] font-bold"><span className="text-[var(--lp-ink-soft)]">Optimization Level</span><span className={isOptimized ? "text-emerald-500" : "text-amber-500"}>{isOptimized ? 'O(N) Optimal' : 'O(N²) Suboptimal'}</span></div>
                          <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className={`h-full rounded-full ${isOptimized ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: isOptimized ? '95%' : '60%' }} /></div>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-7 flex flex-col justify-between gap-4">
                      <div className="border border-[var(--lp-line)] bg-white dark:bg-[#111216] rounded-xl p-5 shadow-sm space-y-4 flex-1 overflow-y-auto max-h-[320px]">
                        <div className="space-y-1">
                          <h4 className="text-sm font-bold text-[var(--lp-ink)]tracking-wider flex items-center gap-1.5">
                            <Icon name="Zap" size={13} className="text-amber-500" /> Complexity Profile
                          </h4>
                          <p className="text-sm text-[var(--lp-ink-soft)]">
                            Time: <code className="font-mono bg-slate-50 dark:bg-slate-900 px-1.5 py-0.5 rounded text-[var(--lp-ink)]">{isOptimized ? 'O(N)' : 'O(N²)'}</code><br />
                            Space: <code className="font-mono bg-slate-50 dark:bg-slate-900 px-1.5 py-0.5 rounded text-[var(--lp-ink)]">{isOptimized ? 'O(N)' : 'O(1)'}</code>
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <h4 className="text-sm font-bold text-[var(--lp-ink)]tracking-wider flex items-center gap-1.5">
                            <Icon name="Bot" size={13} className="text-emerald-500" /> AI Evaluation Feedback
                          </h4>
                          <p className="text-sm text-[var(--lp-ink-soft)] leading-relaxed">
                            {isOptimized
                              ? "Outstanding solution! You successfully recognized the efficiency bottleneck of a nested loops structure and optimized the search lookup using a Hash Map."
                              : "Excellent bug resolution. Correctly adjusted the inner loop index offset from 'i' to 'i + 1' to resolve the index reuse vulnerability. However, your solution relies on a nested loops iteration, resulting in O(N²) time complexity."}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 justify-end">
                        <button onClick={handleResetPractice} className="px-4 py-2.5 text-xs font-semibold border border-[var(--lp-line)] hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition">Restart Practice</button>
                        <button onClick={() => navigate('/interview-setup')} className="px-5 py-2.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition shadow-sm">Start Real Interview</button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                /* ── PLAYGROUND / INTERACTIVE FLOW ── */
                <motion.div key="interview-playground" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex-grow flex flex-col md:flex-row overflow-hidden border-b border-[var(--lp-line)] bg-slate-50/50 dark:bg-[#07080a]"
                >
                  {/* Left: Problem Statement */}
                  <div className="w-full md:w-[28%] border-r border-[var(--lp-line)] flex flex-col bg-slate-50/50 dark:bg-[#08080a] min-w-0 overflow-hidden">
                    <div className="h-10 border-b border-[var(--lp-line)] px-3 flex items-center bg-white dark:bg-[#111216] select-none flex-shrink-0">
                      <span className="text-xs font-bold text-[var(--lp-ink)]tracking-wider flex items-center gap-1.5">
                        <Icon name="FileText" size={13} className="text-emerald-500" /> Problem Statement
                      </span>
                    </div>
                    <div className="flex-grow p-4 overflow-y-auto leading-relaxed select-text min-h-0 text-[12px] space-y-4 max-h-[420px] font-sans">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <h4 className="text-sm font-bold text-[var(--lp-ink)]">Two Sum</h4>
                          <span className="text-[10px] px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded font-semibold font-mono">Easy</span>
                        </div>
                        <p className="text-[var(--lp-ink-soft)] text-xs leading-relaxed">
                          Given an array of integers <code className="font-mono px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[var(--lp-ink)]">nums</code> and an integer <code className="font-mono px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[var(--lp-ink)]">target</code>, return indices of the two numbers such that they add up to <code className="font-mono px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[var(--lp-ink)]">target</code>.
                        </p>
                        <p className="text-[var(--lp-ink-soft)] text-xs leading-relaxed mt-2">You may assume that each input would have exactly one solution, and you may not use the same element twice.</p>
                      </div>
                      <div className="space-y-2">
                        <h5 className="font-bold text-[11px] text-[var(--lp-ink)] tracking-wide uppercase">Examples</h5>
                        <div className="bg-white dark:bg-slate-900/50 border border-[var(--lp-line)] p-2.5 rounded-lg font-mono text-[11px] space-y-1 select-text">
                          <div><span className="text-slate-400">Input:</span> nums = [2,7,11,15], target = 9</div>
                          <div><span className="text-slate-400">Output:</span> [0,1]</div>
                          <div className="text-[10px] text-slate-400 mt-1 font-sans">Explanation: Because nums[0] + nums[1] == 9, we return [0, 1].</div>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <h5 className="font-bold text-[11px] text-[var(--lp-ink)] tracking-wide uppercase">Constraints</h5>
                        <ul className="list-disc pl-3 text-[10.5px] text-[var(--lp-ink-soft)] space-y-0.5">
                          <li><code className="font-mono text-[var(--lp-ink)]">2 &lt;= nums.length &lt;= 10⁴</code></li>
                          <li><code className="font-mono text-[var(--lp-ink)]">-10⁹ &lt;= nums[i] &lt;= 10⁹</code></li>
                          <li>Only one valid answer exists.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Center: Code Editor */}
                  <div className="w-full md:w-[52%] p-4 bg-white dark:bg-[#0c0d10] flex flex-col justify-between gap-4 min-w-0">
                    <div className="flex items-center justify-between border-b border-[var(--lp-line)] pb-2 select-none flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-xs font-mono text-[var(--lp-ink)] font-semibold">solution.{selectedLanguage === 'python' ? 'py' : selectedLanguage === 'javascript' ? 'js' : selectedLanguage === 'cpp' ? 'cpp' : 'go'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {['python', 'javascript', 'cpp', 'go'].map((lang) => (
                          <button key={lang} onClick={() => handleLangChange(lang)}
                            className={`px-2 py-1 rounded text-xs font-boldtransition ${selectedLanguage === lang ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:text-[var(--lp-ink)] border border-transparent'}`}>
                            {lang === 'javascript' ? 'js' : lang}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex-grow bg-slate-50/50 dark:bg-[#08080a] border border-[var(--lp-line)] rounded-lg p-3 overflow-hidden flex min-h-[300px] relative">
                      <div className="w-full h-full flex items-start overflow-hidden">
                        <div className="text-slate-500/30 text-right pr-3 select-none text-xs font-mono w-6 pt-0.5 leading-relaxed">
                          {code.split('\n').map((_, i) => (<div key={i}>{i + 1}</div>))}
                        </div>
                        <textarea value={code}
                          onChange={(e) => { stopAutoplay(); setCode(e.target.value); }}
                          className="flex-grow h-full bg-transparent text-xs font-mono leading-relaxed text-[var(--lp-ink)]/90 dark:text-slate-200 outline-none border-none resize-none pt-0.5 focus:ring-0 focus:border-none textarea-ide"
                          spellCheck={false} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1 select-none flex-shrink-0">
                      <button onClick={handleRunCode}
                        className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold border border-[var(--lp-line)] rounded-md hover:bg-slate-50 dark:hover:bg-[#111216] transition-colors text-[var(--lp-ink)] ${runState === 'running' ? 'opacity-60 cursor-not-allowed' : ''}`}
                        disabled={runState === 'running'}>
                        <Icon name="Play" size={11} className={runState === 'running' ? 'animate-spin' : ''} /> Run Code
                      </button>
                      <button onClick={handleSubmitCode} className="px-5 py-2 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors shadow-sm">Submit</button>
                    </div>
                  </div>

                  {/* Right: Test Cases */}
                  <div className="w-full md:w-[20%] border-l border-[var(--lp-line)] p-4 bg-slate-50/50 dark:bg-[#08080a] flex flex-col justify-start gap-3 min-w-0">
                    <div className="flex items-center justify-between border-b border-[var(--lp-line)] pb-2 select-none flex-shrink-0">
                      <span className="text-xs font-bold text-[var(--lp-ink)]">Test cases</span>
                      {runState === 'done' && (
                        <span className={`text-xs font-bold ${testCases.every(c => c.status === 'passed') ? 'text-emerald-500' : 'text-amber-500'}`}>
                          {testCases.filter(c => c.status === 'passed').length} / {testCases.length} passed
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 flex-grow overflow-y-auto max-h-[360px]">
                      {runState === 'idle' ? (
                        <p className="text-xs text-[var(--lp-ink-soft)] text-center mt-10">Click Run Code to execute test cases...</p>
                      ) : runState === 'running' ? (
                        Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="flex items-center justify-between py-1.5 border-b border-[var(--lp-line)]/20 animate-pulse">
                            <span className="h-3 w-16 bg-slate-200 dark:bg-slate-800 rounded" />
                            <span className="w-3.5 h-3.5 rounded-full bg-slate-200 dark:bg-slate-800" />
                          </div>
                        ))
                      ) : (
                        testCases.map((tc, idx) => {
                          const isFailed = tc.status === 'failed';
                          return (
                            <div key={tc.id || idx} className="space-y-1 pb-1.5 border-b border-[var(--lp-line)]/30 last:border-0">
                              <div className="flex items-center justify-between text-xs">
                                <span className={`font-semibold ${isFailed ? 'text-red-500' : 'text-[var(--lp-ink)]'}`}>Case {idx + 1}</span>
                                <Icon name={isFailed ? "XCircle" : "CheckCircle2"} size={12} className={isFailed ? "text-red-500" : "text-emerald-500"} />
                              </div>
                              {tc.input && (
                                <div className="font-mono text-[10px] text-[var(--lp-ink-soft)] bg-white dark:bg-[#111216] border border-[var(--lp-line)] p-1.5 rounded space-y-0.5 select-text overflow-hidden">
                                  <div><span className="text-slate-400 font-sans">In:</span> {tc.input}</div>
                                  <div><span className="text-slate-400 font-sans">Expected:</span> {tc.expected}</div>
                                  {isFailed && <div className="text-red-400"><span className="text-slate-400 font-sans">Actual:</span> {tc.actual}</div>}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scorecard Loader Overlay */}
            {isSubmitted && !showReport && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-30 flex flex-col items-center justify-center text-center select-none text-white font-sans">
                <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
                <h4 className="text-sm font-bold tracking-wide">Analyzing Interview Performance</h4>
                <p className="text-xs text-slate-400 mt-1 max-w-[280px]">AI Coach is evaluating correctness, code complexity, and communication skills...</p>
              </div>
            )}

            {/* Timeline Footer */}
            <div className="relative p-6 bg-white dark:bg-[#111216] border-t border-[var(--lp-line)] overflow-hidden select-none flex-shrink-0">
              <div className="absolute top-9 left-[14%] right-[14%] h-[1.5px] bg-emerald-600/20 -z-0" />
              <div className="grid grid-cols-4 gap-1 relative z-10 font-sans">
                {[
                  { icon: 'MessageSquare', label: '1. Question', sub: 'AI interviewer asks', active: true },
                  { icon: 'Code', label: '2. Code', sub: 'You write in IDE', active: autoplayStep >= 1 },
                  { icon: 'Play', label: '3. Test', sub: 'Run test cases', active: autoplayStep >= 3 },
                  { icon: 'FileSpreadsheet', label: '4. Score', sub: 'Get scorecard', active: autoplayStep >= 10 },
                ].map((step, idx) => (
                  <div key={idx} className={`flex flex-col items-center transition ${step.active ? 'opacity-100' : 'opacity-50'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-1.5 shadow-sm ${step.active ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500'}`}>
                      <Icon name={step.icon} size={13} />
                    </div>
                    <span className={`text-xs font-bold ${step.active ? 'text-[var(--lp-ink)]' : 'text-[var(--lp-ink-soft)]'}`}>{step.label}</span>
                    <span className="text-[10.5px] text-[var(--lp-ink-soft)] text-center hidden sm:inline">{step.sub}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </motion.div>

        {/* Ticker Strip */}
        <div className="mt-14 overflow-hidden border-y border-[var(--lp-line)] py-4">
          <div className="lp-marquee flex w-max gap-10">
            {[...tickerItems, ...tickerItems].map((item, i) => (
              <span key={`${item}-${i}`} className="whitespace-nowrap font-mono text-xs font-medium tracking-[0.15em] text-[var(--lp-ink-soft)]">
                {item}
                <span className="mx-10 text-[var(--lp-accent)]">◆</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
